import React, { useState, useEffect } from 'react';
import {
  Users, FileText, Eye, Clock, CheckCircle, XCircle,
  Plus, Settings, BarChart3, Upload, Folders,
  AlertCircle, TrendingUp, Calendar, Package, Zap
} from 'lucide-react';
import CMSAuth from '../components/CMSAuth';
import { useAuth } from '../contexts/AuthContext';
import { cmsFetch } from '../lib/cmsApi';

const CMSDashboard = () => {
  const { user: authUser, isAuthenticated } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);

  const fetchAnalytics = async () => {
    try {
      const response = await cmsFetch('/api/cms/analytics/overview');

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        console.error('Failed to fetch analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAccess = () => {
    if (isAuthenticated) {
      setUser({ name: authUser?.displayName || authUser?.email, role: 'admin' });
      setHasAccess(true);
      fetchAnalytics();
    } else {
      setHasAccess(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAccess();
  }, [isAuthenticated]);

  if (!hasAccess) {
    return <CMSAuth onAuthenticated={() => checkAccess()} />;
  }

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue" }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <h3 className="text-sm font-medium text-gray-500">{title}</h3>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const QuickAction = ({ icon: Icon, title, description, onClick, color = "blue" }) => (
    <button
      onClick={onClick}
      className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all duration-200"
    >
      <div className="flex items-start">
        <div className={`flex-shrink-0 p-2 rounded-md bg-${color}-100`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        <div className="ml-3">
          <h4 className="text-sm font-medium text-gray-900">{title}</h4>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
    </button>
  );

  const RecentItem = ({ title, status, date, type, href }) => (
    <div
      className={`flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0 ${href ? 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded transition-colors' : ''}`}
      onClick={() => href && (window.location.href = href)}
    >
      <div className="flex-1">
        <h4 className={`text-sm font-medium ${href ? 'text-blue-700 hover:text-blue-900' : 'text-gray-900'}`}>{title}</h4>
        <p className="text-xs text-gray-500">{type} • {date}</p>
      </div>
      <span className={`px-2 py-1 text-xs rounded-full ${
        status === 'published' ? 'bg-green-100 text-green-800' :
        status === 'approved' ? 'bg-blue-100 text-blue-800' :
        status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
        status === 'draft' ? 'bg-gray-100 text-gray-800' :
        'bg-red-100 text-red-800'
      }`}>
        {status.replace('_', ' ')}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading CMS Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Wanderloop CMS</h1>
              <p className="text-sm text-gray-500">Content Management System</p>
            </div>
            <div className="flex items-center space-x-3">
              {authUser?.photoURL && (
                <img src={authUser.photoURL} alt="" className="w-8 h-8 rounded-full object-cover" />
              )}
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-600" onClick={() => window.location.href = '/account/settings'}>
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FileText}
            title="Total Walks"
            value={analytics?.total_walks || 0}
            subtitle="Published content"
            color="blue"
          />
          <StatCard
            icon={Clock}
            title="Pending Review"
            value={analytics?.pending_reviews || 0}
            subtitle="Awaiting approval"
            color="yellow"
          />
          <StatCard
            icon={CheckCircle}
            title="Published"
            value={analytics?.status_counts?.published || 0}
            subtitle="Live content"
            color="green"
          />
          <StatCard
            icon={Users}
            title="Contributors"
            value={analytics?.total_contributors || 0}
            subtitle="Active users"
            color="purple"
          />
        </div>

        {/* Content Status Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Content Status Overview</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(analytics?.status_counts || {}).map(([status, count]) => (
                <div key={status} className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-semibold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-500 capitalize">{status.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Walk Types</h3>
            <div className="space-y-3">
              {Object.entries(analytics?.type_counts || {}).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 capitalize">{type.replace('_', ' ')}</span>
                  <span className="text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <QuickAction
                icon={Plus}
                title="Create New Walk"
                description="Add a new walk to the content library"
                onClick={() => window.location.href = '/cms/walks/create'}
                color="blue"
              />
              <QuickAction
                icon={Eye}
                title="Review Content"
                description={`${analytics?.pending_reviews || 0} items awaiting review`}
                onClick={() => window.location.href = '/cms/walks?status=pending_review'}
                color="yellow"
              />
              <QuickAction
                icon={Upload}
                title="Media Library"
                description="Upload and manage walk images"
                onClick={() => window.location.href = '/cms/media'}
                color="green"
              />
              <QuickAction
                icon={Users}
                title="Manage Users"
                description="User roles and permissions"
                onClick={() => window.location.href = '/cms/users'}
                color="purple"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
              <button className="text-sm text-blue-600 hover:text-blue-800" onClick={() => window.location.href = '/cms/walks'}>View all</button>
            </div>
            <div className="space-y-1">
              {analytics?.recent_walks?.slice(0, 5).map((walk, index) => (
                <RecentItem
                  key={walk.id}
                  title={walk.title}
                  status={walk.status}
                  date={new Date(walk.updated_at).toLocaleDateString()}
                  type="Walk"
                  href={`/walks/${walk.country || 'us'}/${walk.city || 'los-angeles'}/${walk.neighborhood || 'explore'}/${walk.slug}`}
                />
              )) || (
                <p className="text-sm text-gray-500">No recent activity</p>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { name: 'All Walks', href: '/cms/walks', icon: FileText },
                { name: 'Bulk Generate', href: '/cms/bulk-generate', icon: Zap },
                { name: 'Gear', href: '/cms/gear', icon: Package },
                { name: 'Analytics', href: '/cms/advanced', icon: BarChart3 },
                { name: 'Media Library', href: '/cms/media', icon: Folders },
                { name: 'Users', href: '/cms/users', icon: Users }
              ].map((tab) => (
                <button
                  key={tab.name}
                  className="flex items-center py-4 px-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  onClick={() => window.location.href = tab.href}
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
          
          <div className="p-6">
            <div className="text-center py-12">
              <Folders className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Welcome to Wanderloop CMS</h3>
              <p className="text-gray-500 mb-6">Manage your walk content, review submissions, and monitor performance.</p>
              <button 
                onClick={() => window.location.href = '/cms/walks/create'}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Your First Walk
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMSDashboard;